import { useState } from 'react';
import { CheckCircle2, Download, FileSignature, ShieldAlert } from 'lucide-react';
import { DocumentUploadButton } from '../employees/DocumentUploadButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Step = ({ n, done, active, title, desc, children, testId }) => (
  <li className={`flex gap-3 rounded-lg border p-3 transition-colors ${done ? 'border-emerald-200 bg-emerald-50/60' : active ? 'border-amber-300 bg-white shadow-sm' : 'border-amber-100 bg-white/60'}`} data-testid={testId} data-done={done}>
    {done
      ? <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
      : <span className={`h-6 w-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${active ? 'bg-amber-500 text-white animate-pulse' : 'bg-amber-100 text-amber-700'}`}>{n}</span>}
    <div className="flex-1 min-w-0 space-y-2">
      <div>
        <p className={`text-sm font-semibold ${done ? 'text-emerald-800' : 'text-slate-800'}`}>{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      {children}
    </div>
  </li>
);

const KINDS = {
  kvkk: { renewal: 'kvkk_renewal', flag: 'kvkk_doc_renewal_required', docKey: 'kvkk_taahhutnamesi', path: 'kvkk-template', title: 'KVKK Taahhütnamesi', doc: 'taahhütname', button: 'Taahhütnameyi İndir' },
  aydinlatma: { renewal: 'aydinlatma_renewal', flag: 'aydinlatma_renewal_required', docKey: 'aydinlatma_yukumlulugu', path: 'aydinlatma-template', title: 'Aydınlatma Yükümlülüğü belgesi', doc: 'Aydınlatma Yükümlülüğü belgesi', button: 'Belgeyi İndir' },
};

// Yeni KVKK / Aydınlatma Yükümlülüğü şablonu yayınlandığında firmaya adım adım yol gösterir
export const KvkkRenewalBanner = ({ company, onChanged }) => (
  <>{Object.keys(KINDS).filter(k => company?.[KINDS[k].flag]).map(k => <RenewalCard key={k} kind={k} company={company} onChanged={onChanged} />)}</>
);

const RenewalCard = ({ company, onChanged, kind }) => {
  const K = KINDS[kind];
  const renewal = company[K.renewal];
  const key = `${kind}-renewal-downloaded-${renewal?.version_id}`;
  const [downloaded, setDownloaded] = useState(() => !!localStorage.getItem(key));

  const markDownloaded = () => { localStorage.setItem(key, '1'); setDownloaded(true); };

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-5 space-y-4" data-testid={`${kind}-renewal-banner`} data-allow-pending>
      <div className="flex gap-3">
        <ShieldAlert className="h-6 w-6 text-amber-600 shrink-0" />
        <div>
          <h2 className="font-semibold text-amber-900">Yeni {K.title} yayınlandı (v{renewal.version})</h2>
          <p className="text-sm text-amber-800">İmzalı yeni {K.doc} yüklenene kadar <b>yeni çalışma izni talebi oluşturamazsınız.</b> Aşağıdaki 3 adımı izleyin:</p>
        </div>
      </div>
      <ol className="space-y-2">
        <Step n={1} done={downloaded} active={!downloaded} title="Yeni şablonu indirin" desc={`Güncel ${K.title} şablonunu bilgisayarınıza indirin.`} testId="kvkk-renewal-step-download">
          <a href={`${API}/public/${K.path}`} onClick={markDownloaded} className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800" data-testid={`${kind}-renewal-download`}>
            <Download className="h-3.5 w-3.5" />{K.button}
          </a>
        </Step>
        <Step n={2} done={false} active={downloaded} title="Doldurun ve imzalayın" desc="Belgedeki alanları doldurun, firma kaşesi ve yetkili imzası ile onaylayın, ardından tarayın veya fotoğrafını çekin." testId="kvkk-renewal-step-sign">
          <span className="inline-flex items-center gap-1 text-xs text-slate-500"><FileSignature className="h-3.5 w-3.5" />Kaşe + imza zorunludur</span>
        </Step>
        <Step n={3} done={false} active={downloaded} title="İmzalı belgeyi yükleyin" desc="PDF veya fotoğraf olarak yükleyin. Yükledikten hemen sonra talep oluşturabilirsiniz; belge İSG birimi tarafından ayrıca incelenir." testId="kvkk-renewal-step-upload">
          <div className="flex">
            <DocumentUploadButton entityType="companies" entityId={company.id} docKey={K.docKey} hasFile={false} onUploaded={onChanged} label="İmzalı Belgeyi Yükle" />
          </div>
        </Step>
      </ol>
    </div>
  );
};
